/** Cognito Auth */
import { baseConfig as awsconfig, logger, shortUuid } from '@tunasong/models'
import { AuthTokenError } from './auth-error.js'
import type { AuthData } from './auth-data.js'
import { decodeOAuthData } from './decode-oauth.js'
import { clearJWT, getJWT, setJWT } from './jwt.js'
import { encodeLoginState } from './login-state-schema.js'
import { pkce } from './pkce.js'
import type { PKCE } from './pkce.js'
import { purgeUserData } from './purge-user-data.js'

const {
  userPoolWebClientId,
  oauth: { scope, domain, loginProxy, loginWithCodeUrl, refreshTokenUrl, redirectSignIn, logoutProxy, redirectSignOut },
} = awsconfig.Auth

const CODE_CHALLENGE_KEY = 'tunasong.auth.codechallenge'
const STATE_KEY = 'tunasong.auth.state'

function getUrlParameter(name: string) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
  const results = regex.exec(location.search)
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

export const authorize = async ({ signup = false }: { signup?: boolean } = {}) => {
  const scopeString = scope.join(' ')

  const authState = encodeLoginState({
    clientCode: shortUuid(),
    callbackUrl: redirectSignIn,
  })

  // https://tools.ietf.org/html/rfc7636#appendix-A
  const codePair = await pkce()
  localStorage.setItem(CODE_CHALLENGE_KEY, JSON.stringify(codePair))
  localStorage.setItem(STATE_KEY, authState)

  const url = `https://${domain}/${signup ? 'signup' : 'oauth2/authorize'}?redirect_uri=${encodeURIComponent(
    loginProxy
  )}&response_type=code&client_id=${encodeURIComponent(
    userPoolWebClientId
  )}&identity_provider=COGNITO&scopes=${encodeURIComponent(scopeString)}&state=${encodeURIComponent(
    authState
  )}&code_challenge=${codePair.challenge}&code_challenge_method=S256`

  logger.debug('Redirecting to Cognito', url)

  document.location.replace(url)
}

/** Best-effort read of the `error` field from an OAuth error response body. */
const readOAuthErrorCode = async (result: Response): Promise<string | undefined> => {
  try {
    const body = (await result.json()) as { error?: string }
    return typeof body.error === 'string' ? body.error : undefined
  } catch {
    return undefined
  }
}

/**
 * W2 (docs/SECURITY.md): exchange a request against a same-origin auth-proxy
 * token endpoint. The proxy holds the refresh token in an HttpOnly cookie
 * (`credentials: 'include'` sends/receives it) and returns only the short-lived
 * id/access tokens — the browser never sees a refresh token.
 *
 * The proxy forwards Cognito's OAuth error code + status, so a `400
 * invalid_grant` still becomes a permanent `AuthTokenError` and everything else
 * transient (GH #1538) — the classification that keeps AuthGuard from logging
 * out a user on a transient blip (GH #1469).
 */
const requestProxyTokens = async (url: string, init?: RequestInit): Promise<AuthData> => {
  const result = await fetch(url, { credentials: 'include', ...init })
  if (result.status !== 200) {
    const oauthError = await readOAuthErrorCode(result)
    throw new AuthTokenError(result.status, oauthError)
  }
  const json = (await result.json()) as {
    idToken: string
    accessToken: string
    expiresIn: number
  }
  return decodeOAuthData({ idToken: json.idToken, accessToken: json.accessToken, expiresIn: json.expiresIn })
}

/** Login using code in the URL - i.e., from a OAuth callback */
export const loginWithCode = async (): Promise<AuthData> => {
  const code = getUrlParameter('code')
  const stateBase64 = getUrlParameter('state')
  const stateValBase64 = localStorage.getItem(STATE_KEY)
  /** We remove immediately - loginWithCode is valid one roundtrip only */
  localStorage.removeItem(STATE_KEY)

  if (stateBase64 && stateBase64 !== stateValBase64) {
    throw new Error(`State in OAuth callback ${stateBase64} does not match stored value ${stateValBase64}`)
  }

  const challenge = localStorage.getItem(CODE_CHALLENGE_KEY)
  if (!challenge) {
    throw new Error('Auth: Code Challenge not found in localStorage')
  }
  localStorage.removeItem(CODE_CHALLENGE_KEY)

  const codePair = JSON.parse(challenge) as PKCE

  /**
   * W2: POST the code + PKCE verifier to the auth-proxy instead of exchanging
   * with Cognito directly. The proxy performs the exchange server-side and sets
   * the refresh token as an HttpOnly cookie; the response body carries only the
   * short-lived tokens. The `redirect_uri` is applied server-side (it must match
   * the registered login-proxy URL), so the client does not send it.
   */
  const token = await requestProxyTokens(loginWithCodeUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code, codeVerifier: codePair.verifier }),
  })
  setJWT(token)
  return token
}

const getSessionFromStorage = () => {
  const config = typeof localStorage !== 'undefined' ? getJWT() : null
  return config
}

export const isSessionExpired = (session?: AuthData | null) => {
  if (!session) {
    return true
  }
  const jwtExp = session.idToken.payload.exp
  /** Date.now() is in millis */
  const threeSecondsFromNow = Date.now() / 1000 + 3

  const expired = jwtExp ? jwtExp <= threeSecondsFromNow : true

  return expired
}

/** `true` if the client has a session. It may be expired. However, having a session signals the user is logged in, and can have a refresh token set in a server cookie */
export const hasSession = () => Boolean(getSessionFromStorage())

/** Get the session. If refreshExpired is set to true (default), try to refresh expired sessions by using the refreshToken. */
export const getSession = async (refreshIfExpired = true) => {
  let config = getSessionFromStorage()
  if (!config) {
    return null
  }
  /** Check if the session needs to be refreshed. expires is the time (in millis) that the token expires */
  if (refreshIfExpired && isSessionExpired(config)) {
    config = await refreshOAuthSession(config)
  }
  return config
}

export const clearSession = clearJWT

/**
 * W2: refresh the session via the auth-proxy. The refresh token is in the
 * HttpOnly cookie (sent by `credentials: 'include'`), so nothing from the
 * previous session is needed — the `_config` parameter is retained only for
 * call-site compatibility with `getSession`. The proxy re-sets the (possibly
 * rotated) cookie itself; the client just stores the fresh short-lived tokens.
 */
export const refreshOAuthSession = async (_config?: AuthData | null) => {
  const newConfig = await requestProxyTokens(refreshTokenUrl, { method: 'POST' })
  setJWT(newConfig)
  return newConfig
}

export const logout = async () => {
  /**
   * GH #1909: remove the user's cached data and offline queue BEFORE leaving —
   * once the navigation starts, this page cannot finish the purge.
   */
  await purgeUserData()
  const logoutState = encodeLoginState({
    clientCode: shortUuid(),
    callbackUrl: redirectSignOut,
  })
  /**
   * Redirect to the logout proxy with state. The proxy stashes the state in a
   * same-site cookie scoped to its own domain, then bounces the browser
   * through Cognito's /logout (so the cookie-clearing Set-Cookie response
   * lands on the auth domain) and back to itself, where it reads the cookie
   * and forwards to the originating app's logout page.
   */
  const url = `${logoutProxy}?state=${encodeURIComponent(logoutState)}`
  document.location.replace(url)
}

export const getCognitoLogoutURL = () => {
  const url = `https://${domain}/logout?client_id=${userPoolWebClientId}&logout_uri=${encodeURIComponent(logoutProxy)}`
  return url
}
